import MedicationCard from '../dashboard_cards/MedicationCard';
import EncountersCard from '../dashboard_cards/EncountersCard';
import VitalsCard from '../dashboard_cards/VitalsCard';
import LabResultCard from '../dashboard_cards/LabResultCard';
import ImmunizationsCard from '../dashboard_cards/ImmunizationsCard';
import ProceduresCard from '../dashboard_cards/ProceduresServiceReqCard';
import AppointmentsCard from '../dashboard_cards/AppointmentsCard';
import NotesCard from '../dashboard_cards/ClinicalNotesCard';
import CoverageCard from '../dashboard_cards/CoverageCard';
import { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { apiGet } from "src/utils/api";
import { DashboardCard, TabKey, Appointment, LabResult, Encounter, ExplanationOfBenefit } from '../../shared/types';
import Loading from '../Loading';
import { useAuth } from '../../contexts/AuthContext';
import { InformationCircleIcon, ChevronDownIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import PatientInfo from '../dashboard_cards/PatientInfo';
import { useCurrentOrganization, useOrganizationContext } from 'src/contexts/OrganizationContext';
import {
  CalendarIcon,
  BeakerIcon,
  HeartIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
  DocumentMagnifyingGlassIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import { useConnectionStatusPortal } from 'src/hooks/useConnectionStatusPortal';
import { useVerification } from 'src/contexts/VerificationContext';
import PatientLookupModal from '../modals/PatientLookupModal';
import YearFilter from '../dashboard_cards/YearFilter';
import TableDateTime from '../table/TableDateTime';

type Tab = {
  key: TabKey;
  label: string;
  icon: React.ElementType;
};

const tabs: Tab[] = [
  { key: TabKey.APPOINTMENTS, label: 'Appointments', icon: CalendarIcon },
  { key: TabKey.MEDICATIONS, label: 'Medication', icon: BeakerIcon },
  { key: TabKey.VITALS, label: 'Vital Signs', icon: HeartIcon },
  { key: TabKey.ENCOUNTERS, label: 'Encounters', icon: ClipboardDocumentListIcon },
  { key: TabKey.NOTES, label: 'Clinical Notes', icon: PencilSquareIcon },
  { key: TabKey.PROCEDURES, label: 'Procedures', icon: DocumentTextIcon },
  { key: TabKey.IMMUNIZATIONS, label: 'Immunizations', icon: ShieldCheckIcon },
  { key: TabKey.LAB_RESULTS, label: 'Lab Results', icon: DocumentMagnifyingGlassIcon },
  { key: TabKey.COVERAGE, label: 'Coverage', icon: CurrencyDollarIcon },
];

const DashboardPage = () => {
  const organization = useCurrentOrganization();
  const { patientId } = useOrganizationContext();
  const { blockIfNotConnected } = useConnectionStatusPortal();
  blockIfNotConnected();
  const [ dashboardCards, setDashboardCards ] = useState<DashboardCard[]>([]);
  const [ isLoading, setIsLoading ] = useState(false);
  const [ activeTab, setActiveTab ] = useState<TabKey>(TabKey.APPOINTMENTS);
  const [ showPatientLookup, setShowPatientLookup ] = useState(false);
  const { isDemoSession } = useAuth();
  const { isVerified } = useVerification();
  const currentYear = new Date().getFullYear();
  const [ yearFrom, setYearFrom ] = useState<string>((currentYear - 4).toString());
  const [ yearTo, setYearTo ] = useState<string>(currentYear.toString());
  const setYearRange = useCallback((range: { yearFrom: string; yearTo: string }) => {
    setYearFrom(range.yearFrom);
    setYearTo(range.yearTo);
  }, []);
  const [ nextAppointment, setNextAppointment ] = useState<Appointment | null>(null);
  const [ latestLab, setLatestLab ] = useState<LabResult | null>(null);
  const [ lastActiveClaim, setLastActiveClaim ] = useState<ExplanationOfBenefit | null>(null);
  const [ refreshKey, setRefreshKey ] = useState(0);
  const [ notesEncounterFilter, setNotesEncounterFilter ] = useState<Encounter | null>(null);
  const [ proceduresEncounterFilter, setProceduresEncounterFilter ] = useState<Encounter | null>(null);
  const [ dropdownOpen, setDropdownOpen ] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownContentRef = useRef<HTMLUListElement>(null);
  const [ dropdownPosition, setDropdownPosition ] = useState({ top: 0, left: 0, openAbove: false, maxHeight: 320 });

  const updateDropdownPosition = useCallback(() => {
    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const gap = 8;
      const preferredMax = 320;
      const spaceBelow = window.innerHeight - rect.bottom - gap;
      const spaceAbove = rect.top - gap;
      const openAbove = spaceBelow < preferredMax && spaceAbove > spaceBelow;
      const maxHeight = openAbove
        ? Math.min(preferredMax, spaceAbove)
        : Math.min(preferredMax, spaceBelow);
      setDropdownPosition({
        top: openAbove ? rect.top : rect.bottom,
        left: rect.left,
        openAbove,
        maxHeight: Math.max(120, maxHeight),
      });
    }
  }, []);

  useLayoutEffect(() => {
    if (!dropdownOpen) return;
    updateDropdownPosition();
    const onScrollOrResize = () => updateDropdownPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [ dropdownOpen, updateDropdownPosition ]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current && !dropdownRef.current.contains(target) &&
        dropdownContentRef.current && !dropdownContentRef.current.contains(target)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectPatient = (patientId: string) => {
    if (organization) {
      window.location.href = `/${organization.id}/dashboard/${patientId}`;
    }
  };

  const getTabEnabled = useCallback((key: TabKey) => {
    switch (key) {
      case TabKey.APPOINTMENTS:
        return true;
      case TabKey.MEDICATIONS:
        return dashboardCards.find(card => card.title === 'Medications')?.enabled;
      case TabKey.VITALS:
        return dashboardCards.find(card => card.title === 'Vital Signs')?.enabled;
      case TabKey.ENCOUNTERS:
        return dashboardCards.find(card => card.title === 'Encounters')?.enabled;
      case TabKey.NOTES:
        return dashboardCards.find(card => card.title === 'Clinical Notes')?.enabled;
      case TabKey.PROCEDURES:
        return dashboardCards.find(card => card.title === 'Procedures')?.enabled;
      case TabKey.IMMUNIZATIONS:
        return dashboardCards.find(card => card.title === 'Immunizations')?.enabled;
      case TabKey.LAB_RESULTS:
        return dashboardCards.find(card => card.title === 'Lab Results')?.enabled;
      case TabKey.COVERAGE:
        return dashboardCards.find(card => card.title === 'Coverage')?.enabled;
      default:
        return false;
    }
  }, [ dashboardCards ]);

  useEffect(() => {
    const fetchNextAppointment = async () => {
      if (!organization || !patientId || !isVerified) return;

      try {
        const now = new Date();
        const startDate = now.toISOString().replace(/(\.\d+)?Z$/, 'Z');
        const endDate = new Date(now.getFullYear() + 1, 11, 31, 23, 59, 59).toISOString().replace('.000Z', 'Z');
        const response = await apiGet(`/api/${organization.id}/patients/${patientId}/appointments`, { startDate, endDate });
        const data = await response.json();

        if (!data.error && data.appointments?.length > 0) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          const valid = (data.appointments as Appointment[]).filter(
            a => a.date && a.date !== 'Unknown date'
          );

          const upcoming = valid
            .filter(a => new Date(a.date) >= today)
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

          setNextAppointment(upcoming.length > 0 ? upcoming[0] : null);
        } else {
          setNextAppointment(null);
        }
      } catch (_) {
        setNextAppointment(null);
      }
    };

    fetchNextAppointment();
  }, [ organization, patientId, isVerified ]);

  useEffect(() => {
    const fetchLatestLab = async () => {
      if (!organization || !patientId || !isVerified) return;
      if (!getTabEnabled(TabKey.LAB_RESULTS)) return;

      try {
        const now = new Date();
        const startDate = new Date(now.getFullYear() - 1, 0, 1).toISOString();
        const endDate = now.toISOString();
        const response = await apiGet(`/api/${organization.id}/patients/${patientId}/lab-results`, { startDate, endDate });
        const data = await response.json();

        if (!data.error && data.labResults?.length > 0) {
          const sorted = (data.labResults as LabResult[])
            .filter(r => r.date && r.date !== 'Unknown date')
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

          setLatestLab(sorted.length > 0 ? sorted[0] : null);
        } else {
          setLatestLab(null);
        }
      } catch (_) {
        setLatestLab(null);
      }
    };

    fetchLatestLab();
  }, [ organization, patientId, isVerified, getTabEnabled ]);

  useEffect(() => {
    const fetchLastActiveClaim = async () => {
      if (!organization || !patientId || !isVerified) return;
      if (!getTabEnabled(TabKey.COVERAGE)) return;

      try {
        const response = await apiGet(`/api/${organization.id}/patients/${patientId}/coverage`);
        const data = await response.json();

        if (!data.error && data.explanationOfBenefits?.length > 0) {
          const sorted = (data.explanationOfBenefits as ExplanationOfBenefit[])
            .filter((eob: ExplanationOfBenefit) => eob.created?.date)
            .sort((a: ExplanationOfBenefit, b: ExplanationOfBenefit) => {
              const dA = new Date(a.created!.date + (a.created!.time ? `T${a.created.time}` : '')).getTime();
              const dB = new Date(b.created!.date + (b.created!.time ? `T${b.created.time}` : '')).getTime();
              return dB - dA;
            });
          const active = sorted.find((eob: ExplanationOfBenefit) => eob.status === 'active') ?? sorted[0];
          setLastActiveClaim(active);
        } else {
          setLastActiveClaim(null);
        }
      } catch (_) {
        setLastActiveClaim(null);
      }
    };

    fetchLastActiveClaim();
  }, [ organization, patientId, isVerified, refreshKey, getTabEnabled ]);

  const getClaimAmount = (eob: ExplanationOfBenefit): string => {
    let total = 0;
    let currency = '';
    for (const item of eob.items || []) {
      if (item.quantity != null && item.value != null) {
        if (!currency && item.currency) currency = item.currency;
        total += item.quantity * item.value;
      }
    }
    if (total !== 0) return `${total} ${currency}`.trim();
    const payment = eob.payment?.[0]?.amount;
    if (payment) return `${payment.value} ${payment.currency || ''}`.trim();
    return '—';
  };

  const visibleTabs = tabs.filter(tab => getTabEnabled(tab.key));

  useEffect(() => {
    const fetchDashboardCards = async () => {
      try {
        setIsLoading(true);
        const response = await apiGet(`/api/organizations/${organization.id}/dashboard-cards`);
        const data = await response.json();
        if (!data.error) {
          setDashboardCards(data);
        }
      } catch (_) {
        setDashboardCards([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (organization && patientId) fetchDashboardCards();
  }, [ organization, patientId ]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-stone-50">
        <div className="bg-white shadow-xl rounded-2xl p-10 max-w-md w-full space-y-6 text-center">
          <Loading message="Loading Dashboard..." />
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case TabKey.APPOINTMENTS:
        return (
          <div className="grid grid-cols-1 gap-6">
            <AppointmentsCard yearFrom={yearFrom} yearTo={yearTo} refreshKey={refreshKey} />
          </div>
        );
      case TabKey.MEDICATIONS:
        return (
          <div className="grid grid-cols-1 gap-6">
            <MedicationCard yearFrom={yearFrom} yearTo={yearTo} refreshKey={refreshKey} />
          </div>
        );
      case TabKey.VITALS:
        return (
          <div className="grid grid-cols-1 gap-6">
            <VitalsCard yearFrom={yearFrom} yearTo={yearTo} refreshKey={refreshKey} />
          </div>
        );
      case TabKey.ENCOUNTERS:
        return (
          <div className="grid grid-cols-1 gap-6">
            <EncountersCard
              yearFrom={yearFrom}
              yearTo={yearTo}
              refreshKey={refreshKey}
              onShowClinicalNotes={(encounter) => {
                setActiveTab(TabKey.NOTES);
                setNotesEncounterFilter(encounter);
              }}
              onShowProcedures={(encounter) => {
                setActiveTab(TabKey.PROCEDURES);
                setProceduresEncounterFilter(encounter);
              }}
            />
          </div>
        );
      case TabKey.NOTES:
        return (
          <div className="grid grid-cols-1 gap-6">
            <NotesCard
              yearFrom={yearFrom}
              yearTo={yearTo}
              refreshKey={refreshKey}
              encounterFilter={notesEncounterFilter}
              onClearEncounterFilter={() => setNotesEncounterFilter(null)}
            />
          </div>
        );
      case TabKey.PROCEDURES:
        return (
          <div className="grid grid-cols-1 gap-6">
            <ProceduresCard
              yearFrom={yearFrom}
              yearTo={yearTo}
              refreshKey={refreshKey}
              encounterFilter={proceduresEncounterFilter}
              onClearEncounterFilter={() => setProceduresEncounterFilter(null)}
            />
          </div>
        );
      case TabKey.IMMUNIZATIONS:
        return (
          <div className="grid grid-cols-1 gap-6">
            <ImmunizationsCard yearFrom={yearFrom} yearTo={yearTo} refreshKey={refreshKey} />
          </div>
        );
      case TabKey.LAB_RESULTS:
        return (
          <div className="grid grid-cols-1 gap-6">
            <LabResultCard yearFrom={yearFrom} yearTo={yearTo} refreshKey={refreshKey} />
          </div>
        );
      case TabKey.COVERAGE:
        return (
          <div className="grid grid-cols-1 gap-6">
            <CoverageCard yearFrom={yearFrom} yearTo={yearTo} refreshKey={refreshKey} />
          </div>
        );
      default:
        return null;
    }
  };

  const activeTabLabel = visibleTabs.find(t => t.key === activeTab)?.label ?? activeTab;

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 via-indigo-50 to-purple-50 overflow-x-hidden">
      <div className="flex flex-col lg:flex-row lg:items-start">
        <div className="text-black text-2xl font-bold px-4 lg:px-6 pt-6 pb-0 lg:pb-6 flex-shrink-0">
          <img src="/Patient360.svg" alt="Patient360 Logo" className="w-28" />
        </div>
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-4 sm:py-6 min-w-0">
        {isDemoSession && (
          <div className="mb-6 bg-gradient-to-r from-brand-500 to-indigo-500 text-white rounded-lg shadow-lg p-4">
            <div className="flex items-center">
              <InformationCircleIcon className="w-6 h-6 mr-3 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-lg">Demo Mode</h3>
                <p className="text-sm text-brand-100">
                  You are viewing a demonstration of the patient dashboard with sample data.
                </p>
              </div>
            </div>
          </div>
        )}

        <PatientInfo onPatientLookupClick={() => setShowPatientLookup(true)} />

        { isVerified && (() => {
          const summaryCardCount = [
            getTabEnabled(TabKey.APPOINTMENTS),
            getTabEnabled(TabKey.LAB_RESULTS),
            getTabEnabled(TabKey.COVERAGE),
          ].filter(Boolean).length;
          const gridColsClass = summaryCardCount === 2 ? 'sm:grid-cols-2' : summaryCardCount === 3 ? 'sm:grid-cols-3' : '';
          return (
          <div className={`mt-6 grid grid-cols-1 gap-3 sm:gap-4 ${gridColsClass}`}>
            {getTabEnabled(TabKey.APPOINTMENTS) && (
              <div
                className="relative rounded-xl p-4 sm:p-5 overflow-hidden min-h-[88px] flex flex-col justify-start"
                style={{ background: 'linear-gradient(to top right, #037AC9, #0794F1)' }}
              >
                <img
                  src="/Hexagons.svg"
                  alt=""
                  className="absolute top-0 right-0 pointer-events-none select-none"
                />
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-1">
                    <img src="/Calendar.svg" alt="Calendar" className="w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0" />
                    <span className="text-white font-semibold text-sm sm:text-base leading-tight">Upcoming Appointment</span>
                  </div>
                  <p className="text-white text-sm pl-8 sm:pl-10">{nextAppointment?.date ?? '—'}</p>
                  {nextAppointment?.practitioner && (
                    <p className="text-white/80 text-xs pl-8 sm:pl-10 mt-0.5 truncate">{nextAppointment.practitioner.name}</p>
                  )}
                  {nextAppointment?.location && (
                    <p className="text-white/80 text-xs pl-8 sm:pl-10 truncate">{nextAppointment.location}</p>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setActiveTab(TabKey.APPOINTMENTS);
                      if (nextAppointment?.date) {
                        const y = new Date(nextAppointment.date).getFullYear().toString();
                        setYearFrom(y);
                        setYearTo(y);
                      }
                    }}
                    className="mt-3 ml-8 sm:ml-10 inline-flex items-center rounded-lg border border-white/80 bg-white/10 px-3 py-1.5 text-xs font-medium text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50 touch-manipulation"
                  >
                    View Appointment
                  </button>
                </div>
              </div>
            )}
            {getTabEnabled(TabKey.LAB_RESULTS) && (
              <div
                className="relative rounded-xl p-4 sm:p-5 overflow-hidden min-h-[88px] flex flex-col justify-start"
                style={{ background: 'linear-gradient(to top right, #03A6B7, #018E9D)' }}
              >
                <img
                  src="/Hexagons.svg"
                  alt=""
                  className="absolute top-0 right-0 pointer-events-none select-none"
                />
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-1">
                    <img src="/Lab.svg" alt="Lab Result" className="w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0" />
                    <span className="text-white font-semibold text-sm sm:text-base leading-tight">Previous Labs</span>
                  </div>
                  <p className="text-white text-sm pl-8 sm:pl-10">{latestLab?.date ?? '—'}</p>
                  {latestLab?.name && (
                    <p className="text-white/80 text-xs pl-8 sm:pl-10 mt-0.5 truncate">{latestLab.name}</p>
                  )}
                  {(latestLab?.value || latestLab?.status) && (
                    <p className="text-white/80 text-xs pl-8 sm:pl-10 truncate">
                      {[latestLab.value && `${latestLab.value}${latestLab.unit ? ` ${latestLab.unit}` : ''}`, latestLab.status].filter(Boolean).join(' · ')}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setActiveTab(TabKey.LAB_RESULTS);
                      if (latestLab?.date) {
                        const y = new Date(latestLab.date).getFullYear().toString();
                        setYearFrom(y);
                        setYearTo(y);
                      }
                    }}
                    className="mt-3 ml-8 sm:ml-10 inline-flex items-center rounded-lg border border-white/80 bg-white/10 px-3 py-1.5 text-xs font-medium text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50 touch-manipulation"
                  >
                    View Lab Result
                  </button>
                </div>
              </div>
            )}
            {getTabEnabled(TabKey.COVERAGE) && (
              <div
                className="relative rounded-xl p-4 sm:p-5 overflow-hidden min-h-[88px] flex flex-col justify-start"
                style={{ background: 'linear-gradient(to top right, #DA6207, #F27F27)' }}
              >
                <img
                  src="/Hexagons.svg"
                  alt=""
                  className="absolute top-0 right-0 pointer-events-none select-none"
                />
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-1">
                    <img src="/Billing.svg" alt="Billing" className="w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0" />
                    <span className="text-white font-semibold text-sm sm:text-base leading-tight">Last active claim</span>
                  </div>
                  <div className="pl-8 sm:pl-10">
                    {lastActiveClaim?.created ? (
                      <span className="block text-white text-sm">
                        <TableDateTime
                          date={lastActiveClaim.created.date}
                          className="text-white/90"
                        />
                      </span>
                    ) : (
                      <span className="block text-white text-sm">—</span>
                    )}
                  </div>
                  <p className="text-white/90 text-xs pl-8 sm:pl-10 mt-0.5 tabular-nums">
                    {lastActiveClaim ? getClaimAmount(lastActiveClaim) : '—'}
                  </p>
                  {lastActiveClaim?.insurer?.display && (
                    <p className="text-white/80 text-xs pl-8 sm:pl-10 mt-0.5 truncate">{lastActiveClaim.insurer.display}</p>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setActiveTab(TabKey.COVERAGE);
                      if (lastActiveClaim?.created?.date) {
                        const y = lastActiveClaim.created.date.slice(0, 4);
                        setYearFrom(y);
                        setYearTo(y);
                      }
                    }}
                    className="mt-3 ml-8 sm:ml-10 inline-flex items-center rounded-lg border border-white/80 bg-white/10 px-3 py-1.5 text-xs font-medium text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50 touch-manipulation"
                  >
                    View Coverage
                  </button>
                </div>
              </div>
            )}
          </div>
          );
        })()}

        <div className="mt-6 bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 sm:gap-4 px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-200">
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 min-w-0 flex-1">
              <div className="relative flex items-center gap-2 min-w-0" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => {
                    if (!dropdownOpen) updateDropdownPosition();
                    setDropdownOpen(!dropdownOpen);
                  }}
                  className="flex items-center gap-2 flex-shrink-0 min-h-[44px] min-w-[44px] p-2.5 sm:p-1.5 rounded-lg text-gray-900 font-bold text-lg sm:text-xl hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 truncate touch-manipulation"
                  aria-expanded={dropdownOpen}
                  aria-haspopup="listbox"
                  aria-label="Select section"
                >
                  <span className="truncate">{activeTabLabel}</span>
                  <ChevronDownIcon className="h-5 w-5 flex-shrink-0" />
                </button>
                {dropdownOpen &&
                  ReactDOM.createPortal(
                    <ul
                      ref={dropdownContentRef}
                      className="fixed z-[9999] w-[min(100vw-2rem,16rem)] sm:w-48 overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                      style={{
                        ...(dropdownPosition.openAbove
                          ? { bottom: window.innerHeight - dropdownPosition.top + 8, left: dropdownPosition.left }
                          : { top: dropdownPosition.top + 8, left: dropdownPosition.left }
                        ),
                        maxHeight: dropdownPosition.maxHeight,
                      }}
                      role="listbox"
                    >
                      {visibleTabs.map((tab) => (
                        <li
                          key={tab.key}
                          role="option"
                          aria-selected={activeTab === tab.key}
                          className={`cursor-pointer px-4 py-3 sm:py-2 text-sm min-h-[44px] sm:min-h-0 flex items-center ${activeTab === tab.key ? 'bg-brand-50 font-medium text-brand-700' : 'text-gray-700 hover:bg-gray-50 active:bg-gray-100'}`}
                          onClick={() => {
                            setActiveTab(tab.key);
                            setDropdownOpen(false);
                          }}
                        >
                          {tab.label}
                        </li>
                      ))}
                    </ul>,
                    document.body
                  )}
              </div>
              <div className="flex items-center justify-between gap-3 sm:gap-4 w-full sm:w-auto sm:flex-1 min-w-0 border-t border-gray-200 sm:border-t-0 sm:border-l sm:border-gray-200 pt-3 sm:pt-0 sm:pl-4">
                <YearFilter yearFrom={yearFrom} yearTo={yearTo} setYearRange={setYearRange} />
                <div className="relative group shrink-0">
                  <button
                    type="button"
                    onClick={() => setRefreshKey(k => k + 1)}
                    aria-label="Refresh"
                    className="inline-flex items-center justify-center gap-2 rounded-lg border border-brand-600 bg-white p-2 sm:px-4 sm:py-2 text-sm font-medium text-brand-600 hover:bg-brand-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 touch-manipulation"
                  >
                    <ArrowPathIcon className="h-5 w-5 flex-shrink-0" />
                    <span className="hidden sm:inline">Refresh</span>
                  </button>
                  <span className="block sm:hidden absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    Refresh Data
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            {renderTabContent()}
          </div>
        </div>
        </div>
      </div>
      {showPatientLookup && (
        <PatientLookupModal
          onClose={() => setShowPatientLookup(false)}
          onSelectPatient={handleSelectPatient}
        />
      )}
    </div>
  );
};

export default DashboardPage;
